/* Sign in.
 *
 * Email or mobile in one field: making someone choose which they registered
 * with is a question they should not have to answer. The server accepts either
 * and normalises a mobile to E.164 itself.
 */
import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { api, tokens, ApiError } from '@/api/client'
import type { Session } from '@/types/api'

export function SignInPage() {
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    setError(null)

    try {
      const session = await api.post<Session>(
        '/auth/login',
        { identifier: identifier.trim(), password },
        { auth: false },
      )
      tokens.save(session)
      // A full reload rather than a route change: it clears every cache and
      // guarantees the new session starts from a clean slate.
      //
      // BASE_URL, not a literal '/app/': this app serves from the root on
      // its own subdomain and from /app/ on the legacy host, and Vite is
      // the only thing that knows which build this is.
      window.location.replace(import.meta.env.BASE_URL)
    } catch (err) {
      setError(messageFor(err))
      setBusy(false)
    }
  }

  return (
    /* Same card as the Administration console: 420px, the brand mark INSIDE
       the card above the form, shadow-2 rather than the flatter shadow-1 that
       feed cards use. The two sign-in screens should be indistinguishable. */
    <div className="grid min-h-screen grid-cols-[minmax(0,1fr)] place-items-center bg-canvas p-s5">
      <div className="w-full max-w-[420px] rounded-md bg-surface p-s6 shadow-2">
        <div className="mb-s6 flex flex-col items-center gap-s3">
          <div className="grid h-[60px] w-[60px] place-items-center rounded-pill bg-brand">
            <svg viewBox="0 0 24 24" width="30" height="30" fill="none"
                 stroke="#fff" strokeWidth="2.4" strokeLinecap="round">
              <path d="M12 4v16M6.5 9.5h11" />
            </svg>
          </div>
          <div className="text-center">
            <div className="text-xl font-extrabold tracking-tight text-brand">
              Connect In God
            </div>
          </div>
        </div>

        <form onSubmit={submit} noValidate>
          {error ? (
            <div
              role="alert"
              className="mb-s4 flex items-start gap-s2 rounded border border-danger/30 bg-danger-soft p-s3 text-base text-danger"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <span>{error}</span>
            </div>
          ) : null}

          {/* Same two fields as the Administration console, in the same
              shape: no leading icons, reveal on the password only. */}
          <Field
            label="Mobile or email"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            autoComplete="username"
            autoFocus
            required
          />

          <Field
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            reveal
            autoComplete="current-password"
            required
          />

          {/* The way to a reset. The endpoints have been live all along and
              nothing on the web linked to them, so a forgotten password meant
              no route back into the account at all. */}
          <div className="flex justify-end">
            <Link
              to="/forgot-password"
              className="text-base font-semibold text-brand hover:underline"
            >
              Forgot password?
            </Link>
          </div>

          <Button
            variant="primary"
            size="lg"
            full
            type="submit"
            loading={busy}
            disabled={!identifier.trim() || !password}
            className="mt-s3"
          >
            Sign in
          </Button>
        </form>

        {/* The way to an account. Without this the app had no route to
            signup at all — the only entry was an emailed church link. */}
        <p className="mt-s5 text-center text-base text-ink-2">
          Don't have an account?{' '}
          <Link to="/signup" className="font-semibold text-brand hover:underline">
            Sign up
          </Link>
        </p>
      </div>
    </div>
  )
}

/** Turn a failure into something worth reading.
 *
 *  "Incorrect credentials." is what the API says and it is useless to a person
 *  staring at a form — it does not say what to try next. Note it stays
 *  deliberately vague about WHICH of the two was wrong: saying "no such
 *  account" would let anyone test whether a mobile number is registered.
 */
function messageFor(err: unknown): string {
  if (!(err instanceof ApiError)) return 'Could not sign you in.'

  if (err.status === 0) {
    return 'You appear to be offline. Check your connection and try again.'
  }
  if (err.status === 401 || err.code === 'invalid_credentials') {
    return 'That email or mobile and password do not match. Check both and try again.'
  }
  if (err.code === 'account_disabled') {
    return 'This account has been disabled. Ask your church administrator for help.'
  }
  if (err.status === 429) {
    return 'Too many attempts. Wait a minute before trying again.'
  }
  if (err.status >= 500) {
    return 'Something went wrong at our end. Try again in a moment.'
  }
  return err.message
}
